import React from 'react';
import StatisticsBox from '../components/StatisticsBox';
import ReadinessGauge from '../components/ReadinessGauge';
import DistributionChart from '../components/DistributionChart';
import PerformanceChart from '../components/PerformanceChart';
import styles from './MUMView.module.css';
import layoutStyles from '../WindowReadinessResults.module.css';

const MUMView = ({ data }) => {
  if (!data) return <div className="loading">Loading MUM view...</div>;

  const { mumStats, wowComparison, locations, mum, isFirstUpload } = data;

  // Prepare all locations for chart
  const allLocationsChartData = locations.map((location) => ({
    label: `${location.training_location_name}`,
    value: parseFloat(location.completion_percentage),
  }));

  // Get top 10 locations
  const topLocations = locations.slice(0, 10);
  const topLocationsChartData = topLocations.map((location) => ({
    label: `${location.training_location_name}`,
    value: parseFloat(location.completion_percentage),
  }));

  // Determine action items
  const getActionItems = () => {
    const items = [];
    const completion = parseFloat(mumStats.overall_completion);

    if (isFirstUpload) {
      if (completion >= 80) {
        items.push({ type: 'success', text: 'All locations are compliant - excellent work!' });
      } else if (completion >= 60) {
        items.push({ type: 'warning', text: 'Work with store managers to push locations over 80%' });
        items.push({ type: 'info', text: `Focus on ${mumStats.locations_caution + mumStats.locations_critical} locations` });
      } else {
        items.push({ type: 'critical', text: 'Immediate store visits required for critical locations' });
        items.push({ type: 'critical', text: 'Ensure all employees have access and time to complete training' });
      }
    } else {
      if (wowComparison.trend === 'up') {
        items.push({ type: 'success', text: `Great job! Improved by ${wowComparison.change.toFixed(1)}%` });
        items.push({ type: 'info', text: 'Keep up the momentum' });
      } else if (wowComparison.trend === 'down') {
        items.push({ type: 'warning', text: `Completion declined by ${Math.abs(wowComparison.change).toFixed(1)}%` });
        items.push({ type: 'warning', text: 'Schedule check-ins with declining locations' });
      }

      if (mumStats.locations_critical > 0) {
        items.push({ type: 'critical', text: `${mumStats.locations_critical} locations in critical status` });
      }
    }

    return items;
  };

  const actionItems = getActionItems();

  return (
    <div className={styles.mumView}>
      <div className={styles.viewHeader}>
        <h2>MUM: {mum}</h2>
        {mumStats.director && <p className={styles.directorInfo}>Director: {mumStats.director}</p>}
      </div>

      <StatisticsBox stats={mumStats} wowComparison={wowComparison} />

      <div className={layoutStyles.chartsGrid}>
        <ReadinessGauge
          percentage={parseFloat(mumStats.overall_completion)}
          title="Overall Readiness"
        />
        <DistributionChart
          ready={mumStats.locations_ready}
          caution={mumStats.locations_caution}
          critical={mumStats.locations_critical}
          title="Readiness Distribution"
        />
      </div>

      <div className={layoutStyles.chartsGrid}>
        {allLocationsChartData.length <= 10 ? (
          <PerformanceChart
            data={allLocationsChartData}
            title="All Locations Performance"
          />
        ) : (
          <>
            <PerformanceChart
              data={allLocationsChartData}
              title="All Locations Performance"
            />
            <PerformanceChart
              data={topLocationsChartData}
              title="Top 10 Locations Needing Attention"
            />
          </>
        )}
      </div>

      <div className={layoutStyles.actionItems}>
        <h3>Action Items</h3>
        <ul>
          {actionItems.map((item, index) => (
            <li key={index} className={layoutStyles[`actionItem${item.type.charAt(0).toUpperCase() + item.type.slice(1)}`]}>
              {item.text}
            </li>
          ))}
        </ul>
      </div>

      <div className={layoutStyles.dataTable}>
        <h3>Locations Priority View (Sorted by Lowest Completion)</h3>
        <div className={layoutStyles.tableResponsive}>
          <table>
            <thead>
              <tr>
                <th>Location Name</th>
                <th>Address</th>
                <th>City, State</th>
                <th>Store Manager</th>
                <th>Employee Count</th>
                <th>Completed</th>
                <th>Completion %</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((location, index) => (
                <tr key={index} className={layoutStyles[`row${location.completion_percentage >= 80 ? 'Ready' : location.completion_percentage >= 60 ? 'Caution' : 'Critical'}`]}>
                  <td>{location.training_location_name}</td>
                  <td>{location.address}</td>
                  <td>{location.city}, {location.state}</td>
                  <td>{location.store_manager}</td>
                  <td>{location.employee_count}</td>
                  <td>{location.employees_completed}</td>
                  <td>
                    <span className={layoutStyles.completionBadge}>{location.completion_percentage}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default MUMView;
